import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
// Path imports, not the @mui/icons-material barrel (see EmailPreviewDialog).
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import type { CustomerProfile } from "../../../../utils/customerRules";

// The quote email dialog's notices, action bar and secondary dialogs.

/** What a portal RFQ needs beyond this email (Seven Seas, Archangel, Sinwa). */
export function PortalNotice({ portal }: { portal: CustomerProfile['portal'] }) {
  return (
    <>
      {portal === 'seven_seas' && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Seven Seas portal: the quote goes to Supplier.sevenseas@sevenseasgroup.com only —
          no CC, original subject and Excel filename kept. Send any drawings / certificates /
          datasheets to their user separately after submitting.
        </Alert>
      )}
      {portal === 'archangel' && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Archangel portal RFQ: this email does not by itself submit the bid.
          Make sure the prices, remarks and lead times are on the portal (the link in the
          original email) and that the quotation has been sent to the buyer there.
        </Alert>
      )}
      {portal === 'epbrowser' && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Sinwa portal RFQ: this email does not by itself submit the bid.
          The quote must be entered on the Sinwa portal via the link in the RFQ email -
          automatic portal fill is off until it is approved.
        </Alert>
      )}
    </>
  );
}

interface EmailDialogActionsProps {
  allItemsValidated: boolean;
  isPdfLoading: boolean;
  onPreviewPdf: () => void;
  hasUserEdits: boolean;
  onReset: () => void;
  blockedReason: string | null;
  isSubmitting: boolean;
  onCancel: () => void;
  onSend: () => void;
}

/** Preview PDF and Reset on the left; why Send is blocked, Cancel and Send on the right. */
export function EmailDialogActions(p: EmailDialogActionsProps) {
  return (
    <DialogActions
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1,
        px: { xs: 2, sm: 3 },
        py: 1.5,
        bgcolor: 'grey.50',
        '& > :not(style) ~ :not(style)': { ml: 0 },
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {p.allItemsValidated && (
          <Button
            startIcon={<Visibility />}
            onClick={p.onPreviewPdf}
            disabled={p.isPdfLoading}
          >
            Preview Quote PDF
          </Button>
        )}
        {p.hasUserEdits && (
          <Button
            color="warning"
            onClick={p.onReset}
          >
            Reset to Default
          </Button>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, ml: 'auto', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {p.blockedReason && p.allItemsValidated && !p.isSubmitting && (
          <Typography variant="caption" color="text.secondary" role="status" sx={{ maxWidth: 360 }}>
            {p.blockedReason}
          </Typography>
        )}
        <Button onClick={p.onCancel} disabled={p.isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={p.onSend}
          disabled={p.isSubmitting || !p.allItemsValidated || Boolean(p.blockedReason)}
          startIcon={p.isSubmitting ? <CircularProgress size={20} color="inherit" /> : undefined}
          sx={{ minWidth: 128, fontWeight: 600 }}
        >
          {p.isSubmitting ? 'Sending...' : 'Send Email'}
        </Button>
      </Box>
    </DialogActions>
  );
}

/** The generated quote PDF, full size. */
export function PdfPreviewDialog({ open, onClose, pdfData, fullScreen }: { open: boolean; onClose: () => void; pdfData: string | null; fullScreen: boolean }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
        Quote Preview
        <IconButton onClick={onClose} size="small" aria-label="Close quote preview">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex' }}>
        {pdfData ? (
          <iframe
            src={`data:application/pdf;base64,${pdfData}`}
            style={{ width: '100%', height: fullScreen ? 'calc(100vh - 64px)' : 'min(80vh, 1200px)', border: 0, display: 'block' }}
            title="Quote PDF Preview"
          />
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, width: '100%' }}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** "Reset Email Content?" before the subject and body are regenerated. */
export function ResetConfirmDialog({ open, onCancel, onConfirm }: { open: boolean; onCancel: () => void; onConfirm: () => void }) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs">
      <DialogTitle>Reset Email Content?</DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to reset the email content to its default template? Your changes will be lost.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={onConfirm} color="warning" variant="contained">
          Reset Content
        </Button>
      </DialogActions>
    </Dialog>
  );
}
